import React, { useEffect, useMemo, useState } from 'react';
import { Transaction } from '../../types';
import { useCurrency } from '../../context/CurrencyContext';
import { useExchangeRates } from '../../hooks/useExchangeRates';
import { useSavedSellPlans } from '../../hooks/useSavedPlans';
import { Target, Check, X } from 'lucide-react';

/**
 * Cash-out Goal tracker. Combines REALIZED proceeds (sum of logged SELL
 * transactions — real money) with optional PLANNED proceeds (sum of saved sell
 * plans' expected proceeds — hypothetical, frozen) against a user-set dollar goal.
 *
 * Display only: never affects holdings, cost basis, or P&L. Saved plans are not
 * recalculated — their stored expected proceeds are summed as-is (converted from
 * their frozen currency to USD at current rates so everything shares one unit).
 */

const GOAL_KEY = 'cashOutGoal';            // numeric goal, stored in USD (app base unit)
const PROJ_KEY = 'goalShowProjection';     // '1' | '0'

const BRONZE = '#b89968';
const GREEN = '#6db981';
const STRIPES = `repeating-linear-gradient(45deg, rgba(109,185,129,0.6) 0px, rgba(109,185,129,0.6) 5px, rgba(109,185,129,0.26) 5px, rgba(109,185,129,0.26) 10px)`;

function readGoal(): number | null {
  try {
    const raw = localStorage.getItem(GOAL_KEY);
    if (!raw) return null;
    const n = parseFloat(raw);
    return isFinite(n) && n > 0 ? n : null;
  } catch { return null; }
}
function readProj(): boolean {
  try { return localStorage.getItem(PROJ_KEY) === '1'; } catch { return false; }
}

function Stat({ label, value, sub, color }: { label: string; value: string; sub: string; color?: string }) {
  return (
    <div>
      <p className="uppercase" style={{ fontSize: 9, letterSpacing: '0.08em', color: BRONZE }}>{label}</p>
      <p className="num text-[15px] font-semibold mt-0.5" style={{ color: color ?? 'rgb(var(--sl-100))' }}>{value}</p>
      <p className="text-[10px] mt-0.5" style={{ color: 'var(--color-sub)' }}>{sub}</p>
    </div>
  );
}

export function CashOutGoalCard({ transactions, openSignal }: { transactions: Transaction[]; openSignal?: number }) {
  const { fmt, rate } = useCurrency();
  const { rates } = useExchangeRates();
  const { plans } = useSavedSellPlans();

  const [goalUsd, setGoalUsdState] = useState<number | null>(readGoal);
  const [projOn, setProjOn] = useState<boolean>(readProj);
  const [editing, setEditing] = useState(false);
  const [goalInput, setGoalInput] = useState('');

  const setGoalUsd = (v: number | null) => {
    setGoalUsdState(v);
    try {
      if (v != null) localStorage.setItem(GOAL_KEY, String(v));
      else localStorage.removeItem(GOAL_KEY);
    } catch { /* ignore */ }
  };
  const toggleProj = () => setProjOn((p) => {
    const next = !p;
    try { localStorage.setItem(PROJ_KEY, next ? '1' : '0'); } catch { /* ignore */ }
    return next;
  });

  // ── Realized (USD) — gross proceeds from logged SELLs only (not buys/transfers) ─
  const sells = useMemo(() => transactions.filter((t) => t.type === 'SELL'), [transactions]);
  const realizedUsd = useMemo(() => sells.reduce((s, t) => s + t.amount * t.price, 0), [sells]);
  const sellCount = sells.length;
  const sellCoinCount = useMemo(() => new Set(sells.map((t) => t.coinId)).size, [sells]);

  // ── Planned (USD) — Σ saved sell plans' expected proceeds, frozen currency → USD ─
  const plannedUsd = useMemo(
    () => plans.reduce((s, p) => s + (p.expectedProceeds || 0) / (rates[p.currency] ?? 1), 0),
    [plans, rates]
  );

  // ── Derived ──────────────────────────────────────────────────────────────────
  const hasGoal = goalUsd != null && goalUsd > 0;
  const g = goalUsd ?? 0;
  const combinedUsd = realizedUsd + (projOn ? plannedUsd : 0);
  const pctRealized = g > 0 ? Math.min(100, (realizedUsd / g) * 100) : 0;
  const pctPlanned  = g > 0 ? Math.max(0, Math.min(100 - pctRealized, (plannedUsd / g) * 100)) : 0;
  const pctCombined = g > 0 ? Math.min(100, (combinedUsd / g) * 100) : 0;
  const remainingUsd = Math.max(0, g - combinedUsd);
  const over = hasGoal && combinedUsd >= g;
  const headlinePct = projOn ? pctCombined : pctRealized;

  const openEdit = () => {
    setGoalInput(goalUsd != null ? String(Number((goalUsd * rate).toFixed(2))) : '');
    setEditing(true);
  };
  const saveGoal = () => {
    const v = parseFloat(goalInput);
    if (isFinite(v) && v > 0) { setGoalUsd(v / rate); setEditing(false); }
  };

  // External trigger (e.g. the dashboard "Set a cash-out goal" empty-state row):
  // each increment of openSignal pops the goal editor.
  useEffect(() => {
    if (openSignal && openSignal > 0) openEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSignal]);

  // ── Small switch ───────────────────────────────────────────────────────────
  const Switch = (
    <button
      type="button"
      role="switch"
      aria-checked={projOn}
      onClick={toggleProj}
      className="inline-flex items-center gap-2"
    >
      <span
        className="relative inline-block transition-colors"
        style={{
          width: 30, height: 17, borderRadius: 9999,
          background: projOn ? GREEN : 'rgb(var(--overlay) / 0.12)',
        }}
      >
        <span
          className="absolute top-1/2 -translate-y-1/2 rounded-full transition-all"
          style={{ width: 13, height: 13, background: '#fff', left: projOn ? 14 : 2 }}
        />
      </span>
      <span className="text-[11px]" style={{ color: 'var(--color-sub)' }}>Show plan projection</span>
    </button>
  );

  return (
    <div
      // Desktop (≥ md): boxed card (inline style below, unchanged). Mobile (< md):
      // drop the box — transparent bg, no border/radius, flush horizontally — and
      // keep only a thin top divider (slate-700/40, matching the stats/holdings),
      // so it sits openly on the page like the other boxless dashboard sections.
      className="max-md:!bg-transparent max-md:!rounded-none max-md:!border-l-0 max-md:!border-r-0 max-md:!border-b-0 max-md:!border-t max-md:!border-t-slate-700/40 max-md:!px-0 max-md:!pt-5"
      style={{
        background: 'rgb(var(--overlay) / 0.025)',
        border: '0.5px solid rgb(var(--overlay) / 0.06)',
        borderRadius: 12,
        padding: '18px 20px',
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="uppercase font-semibold" style={{ fontSize: 10, letterSpacing: '0.1em', color: BRONZE }}>
            Cash-out goal
          </p>
          {hasGoal && !editing && (
            <>
              <p className="num text-[26px] font-bold leading-tight mt-1" style={{ color: 'rgb(var(--sl-100))' }}>
                {fmt(g)}
              </p>
              <p className="text-[12px] mt-0.5" style={{ color: 'var(--color-sub)' }}>
                {projOn
                  ? `${Math.round(pctCombined)}% covered by realized + planned`
                  : `${Math.round(pctRealized)}% reached from real sells`}
              </p>
            </>
          )}
        </div>

        {hasGoal && (
          <div className="flex flex-col items-end gap-2">
            {!editing && (
              <button
                type="button"
                onClick={openEdit}
                className="text-[11px] px-2.5 py-1 rounded-lg transition-colors"
                style={{ color: 'var(--color-sub)', border: '0.5px solid rgb(var(--overlay) / 0.1)' }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'rgb(var(--sl-100))'; e.currentTarget.style.background = 'rgb(var(--overlay) / 0.04)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-sub)'; e.currentTarget.style.background = 'transparent'; }}
              >
                Edit goal
              </button>
            )}
            {Switch}
          </div>
        )}
      </div>

      {/* Body */}
      {editing ? (
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[160px]">
            <input
              type="number"
              min="0"
              autoFocus
              value={goalInput}
              onChange={(e) => setGoalInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') saveGoal(); if (e.key === 'Escape') setEditing(false); }}
              placeholder="e.g. 50000"
              className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-white/20"
            />
          </div>
          <button
            type="button"
            onClick={saveGoal}
            className="flex items-center gap-1.5 rounded-lg font-medium"
            style={{ background: '#d4b483', color: '#0c0e12', padding: '8px 14px', fontSize: 12 }}
          >
            <Check className="w-3.5 h-3.5" /> Save
          </button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="p-2 rounded-lg"
            style={{ color: 'var(--color-sub)' }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : !hasGoal ? (
        <div className="mt-3 flex flex-col items-start gap-3">
          <p className="text-[13px]" style={{ color: 'var(--color-sub)' }}>
            Set a cash-out goal and track your progress.
          </p>
          <button
            type="button"
            onClick={openEdit}
            className="flex items-center gap-2 rounded-lg font-medium"
            style={{ background: '#d4b483', color: '#0c0e12', padding: '9px 16px', fontSize: 12 }}
          >
            <Target className="w-4 h-4" /> Set a goal
          </button>
        </div>
      ) : (
        <>
          {/* Bar top */}
          <div className="flex items-center justify-between gap-2 mt-4 mb-1.5 flex-wrap">
            <span className="text-[12px] font-medium" style={{ color: over ? GREEN : 'rgb(var(--sl-100))' }}>
              {over ? 'Goal reached 🎉' : `${Math.round(headlinePct)}% ${projOn ? 'projected' : 'reached'}`}
            </span>
            <span className="num text-[12px]" style={{ color: 'var(--color-sub)' }}>
              {fmt(projOn ? combinedUsd : realizedUsd)} of {fmt(g)}
            </span>
          </div>

          {/* Bar */}
          <div
            className="flex overflow-hidden"
            style={{ height: 12, borderRadius: 9999, background: 'rgb(var(--overlay) / 0.06)' }}
          >
            <div style={{ width: `${pctRealized}%`, background: GREEN }} className="transition-all duration-300" />
            {projOn && <div style={{ width: `${pctPlanned}%`, backgroundImage: STRIPES }} className="transition-all duration-300" />}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-x-4 gap-y-1.5 mt-2.5 flex-wrap text-[11px]">
            <span className="inline-flex items-center gap-1.5" style={{ color: 'var(--color-sub)' }}>
              <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: GREEN }} />
              Realized <span className="num" style={{ color: 'rgb(var(--sl-100))' }}>{fmt(realizedUsd)}</span>
            </span>
            {projOn && (
              <span className="inline-flex items-center gap-1.5" style={{ color: 'var(--color-sub)' }}>
                <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundImage: STRIPES }} />
                Planned <span className="num" style={{ color: 'rgb(var(--sl-100))' }}>{fmt(plannedUsd)}</span>
              </span>
            )}
            <span className="inline-flex items-center gap-1.5" style={{ color: 'var(--color-sub)' }}>
              <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: 'rgb(var(--overlay) / 0.12)' }} />
              Remaining <span className="num" style={{ color: 'rgb(var(--sl-100))' }}>{fmt(remainingUsd)}</span>
            </span>
          </div>

          {/* Stats */}
          <div
            className="grid grid-cols-3 gap-4 mt-4 pt-4"
            style={{ borderTop: '0.5px solid rgb(var(--overlay) / 0.06)' }}
          >
            {projOn ? (
              <>
                <Stat label="Realized" value={fmt(realizedUsd)} sub="actual sells" color={GREEN} />
                <Stat label="Planned" value={fmt(plannedUsd)} sub="saved sell plans" />
                <Stat label="Remaining to plan" value={fmt(remainingUsd)} sub="to reach goal" />
              </>
            ) : (
              <>
                <Stat label="Cashed out" value={fmt(realizedUsd)} sub="actual sells" color={GREEN} />
                <Stat label="Remaining" value={fmt(remainingUsd)} sub="to reach goal" />
                <Stat label="Sells logged" value={String(sellCount)} sub={`across ${sellCoinCount} coin${sellCoinCount === 1 ? '' : 's'}`} />
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
